// src/lib/rlu.ts — helpers PURI pentru regimul urbanistic (RLU), partajați server↔client.
// Zero dependințe (importabil din browser): vederea 3D (F10) re-derivă codul UTR și regimul
// de înălțime în client cu ACELEAȘI chei și reguli ca serverul — listele/regulile de aici sunt
// sursa unică; regim.ts le re-exportă ca API-ul server (public.ts, documents.ts) să rămână stabil.

// Cheile-atribut din care se derivă identitatea regimului (căutate case-insensitive prin
// attr_text). Public.ts re-derivă aceleași câmpuri din atributele filtrate F10 — listele
// trebuie să rămână IDENTICE pe toate căile, altfel publicul ar diverge de intern.
// „cod_szf"/„cod_zf" = fallback pentru straturile Ordin 904 fără cod UTR (ZFPropusa ca strat de regim).
// „nume_utr" = ULTIMA rezervă (11.09): ordinea contează — cele patru chei de cod câștigă mereu, deci
// nicio hartă existentă nu-și schimbă comportamentul; se aplică doar când NU există nicio cheie de cod.
// Real: Armeniș, al cărui strat UTR avea `cod_utr` și `nume_UTR` cu valori IDENTICE („M", „Li", „Va",
// „Is"), iar pachetul din 20.08 a păstrat doar `nume_UTR` — codul era acolo, sub alt nume de coloană,
// și regimul îl raporta gol.
export const UTR_COD_KEYS = ["cod_utr", "utr", "cod_szf", "cod_zf", "nume_utr", "zona"]
/** Subzona funcțională (904) de pe poligonul lovit — regulamentul e per SUBZONĂ, nu per UTR. */
export const SZF_KEYS = ["cod_szf", "cod_szf_zrs_d"]

/**
 * Codurile pe care se caută regulamentul, în ordinea preferinței: subzona exactă de pe poligon
 * (`cod_szf` — un UTR „Ip" poate avea SZF09.3 sau SZF09.4, cu indicatori diferiți), apoi codul
 * UTR. Fără dubluri, fără goluri. Folosită de regim.ts pentru indicatori și fișă.
 */
export function regulation_codes(attrs: Record<string, unknown>, cod: string): string[] {
	const out: string[] = []
	for (const c of [attr_text(attrs, SZF_KEYS), cod]) {
		const t = c.trim()
		if (t && !out.includes(t)) {
			out.push(t)
		}
	}
	return out
}
export const DEROGARE_ACT_KEYS = ["act", "denumire", "nume"]
export const DEROGARE_HCL_KEYS = ["hcl", "act_aprobare", "aprobare"]

/** Prima valoare ne-goală dintre cheile date, căutate case-insensitive în atribute. */
export function attr_text(attrs: Record<string, unknown>, keys: string[]): string {
	const low = new Map<string, unknown>()
	for (const [k, v] of Object.entries(attrs)) {
		if (!low.has(k.toLowerCase())) {
			low.set(k.toLowerCase(), v)
		}
	}
	for (const k of keys) {
		const v = low.get(k)
		const s = v == null ? "" : String(v).trim()
		if (s) {
			return s
		}
	}
	return ""
}

/**
 * Rândul cu `utr === cod` dintr-o listă de indicatori. Potrivire EXACTĂ pe cod; fallback:
 * codul fără sufixul numeric (subzonele „In1"/„In2" împart indicatorii lui „In" — real în
 * datele Ștefan cel Mare, unde fișele au In1/In2 dar tabelul de indicatori doar „In").
 * Obiect gol = negăsit (doctrina obiectelor nil — nu null).
 */
export function find_by_utr_code(
	rows: Record<string, unknown>[],
	cod: string,
): Record<string, unknown> {
	const exact = rows.find((r) => String(r.utr ?? "").trim() === cod)
	if (exact) {
		return exact
	}
	const base = cod.replace(/\d+$/, "")
	if (base && base !== cod) {
		const by_base = rows.find((r) => String(r.utr ?? "").trim() === base)
		if (by_base) {
			return by_base
		}
	}
	return {}
}

// ─────────────────────────────────────────────────────────────
// Nomenclatorul de AVIZE (decizia 4.8, URB-031/048): `metadata.rlu.avize`
// ─────────────────────────────────────────────────────────────

/** O intrare din nomenclator: avizele necesare într-o zonă (`cod`) sau peste tot (`cod: "*"`). */
/** Un act din nomenclatorul legislativ al hărții (REQ-URB-004): legi/HG care modifică regimul. */
export interface RegimLegislatie {
	act: string
	descriere: string
	url: string
}

/**
 * Nomenclatorul legislativ, lenient (EAV liber: array, string JSON, null). Definiția UNICĂ a ce e o
 * intrare — o folosesc panoul de regim, contextul actelor (documents.ts) și istoricul regulamentului
 * (lib/rlu_diff), ca aceleași date să nu se citească diferit în trei locuri.
 */
export function parse_legislatie(v: unknown): RegimLegislatie[] {
	let raw = v
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw)
		} catch {
			return []
		}
	}
	if (!Array.isArray(raw)) {
		return []
	}
	const out: RegimLegislatie[] = []
	for (const item of raw) {
		const o = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>
		const act = String(o.act ?? "").trim()
		const descriere = String(o.descriere ?? "").trim()
		const url = String(o.url ?? "").trim()
		if (act || descriere) {
			out.push({ act, descriere, url })
		}
	}
	return out
}

export interface AvizeEntry {
	cod: string // codul UTR / SZF / ZF (potrivit ca indicatorii) sau "*" = general
	avize: string[]
}

/** Codul „oriunde" — avizele generale (ex. Mediu) se adaugă în orice zonă, și în extravilan. */
export const AVIZE_ALL = "*"

/**
 * `metadata.rlu.avize` (unknown din jsonb) → listă tipată, DEFENSIV. Acceptă cheia zonei sub oricare
 * dintre numele `utr` / `cod_zf` / `cod_szf` / `cod` (editorul și tabelele 904 le folosesc pe toate) și
 * `avize` ca listă de texte SAU text unic separat prin `;` / linie nouă (lipit din regulament).
 */
export function parse_avize(v: unknown): AvizeEntry[] {
	let raw = v
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw)
		} catch {
			return []
		}
	}
	if (!Array.isArray(raw)) {
		return []
	}
	const out: AvizeEntry[] = []
	for (const item of raw) {
		if (typeof item !== "object" || item === null || Array.isArray(item)) {
			continue
		}
		const o = item as Record<string, unknown>
		const cod = String(o.utr ?? o.cod_zf ?? o.cod_szf ?? o.cod ?? "").trim()
		const list = Array.isArray(o.avize)
			? o.avize
			: typeof o.avize === "string"
				? o.avize.split(/[;\n]+/)
				: []
		const avize = list
			.map((a) => (a == null ? "" : String(a).trim()))
			.filter((a, i, arr) => a !== "" && arr.indexOf(a) === i)
		if (cod && avize.length > 0) {
			out.push({ cod, avize })
		}
	}
	return out
}

/**
 * Avizele necesare pentru codurile date (în ordinea preferinței din `regulation_codes`: SZF, apoi UTR),
 * fiecare cu ACEEAȘI potrivire ca indicatorii — exactă, apoi fără sufixul numeric (In1 → In) — plus
 * cele generale (`*`) la final. Fără dubluri, ordinea păstrată. `codes` gol = doar cele generale.
 */
export function avize_for_codes(entries: AvizeEntry[], codes: string[]): string[] {
	const out: string[] = []
	const add = (list: string[]) => {
		for (const a of list) {
			if (!out.includes(a)) {
				out.push(a)
			}
		}
	}
	for (const cod of codes) {
		const c = cod.trim()
		if (!c) {
			continue
		}
		const exact = entries.filter((e) => e.cod === c)
		if (exact.length > 0) {
			exact.forEach((e) => add(e.avize))
			continue
		}
		const base = c.replace(/\d+$/, "")
		if (base && base !== c) {
			entries.filter((e) => e.cod === base).forEach((e) => add(e.avize))
		}
	}
	entries.filter((e) => e.cod === AVIZE_ALL).forEach((e) => add(e.avize))
	return out
}

/**
 * Parser de regim de înălțime românesc → metri estimați (F10-T1, REQ-M3D-001).
 * Acceptă formele reale din RLU: "P", "P+2", "P+1E", "P+1+M", "P+1E+M", "D+P+M", "S+P+3+M".
 * Convenție (plan/F10: etaj 3m, restul estimări documentate): parter 3m, etaj 3m,
 * demisol +1m (partea vizibilă), mansardă +2m. 0 = neinterpretabil (ex. „Nu este cazul").
 */
export function regim_to_meters(raw: unknown): number {
	if (typeof raw !== "string") {
		return 0
	}
	const s = raw.toUpperCase().replace(/\s+/g, "")
	if (!s.includes("P")) {
		return 0
	}
	let h = 3
	const et = s.match(/P\+(\d+)/) ?? s.match(/(\d+)E/)
	if (et) {
		h += parseInt(et[1], 10) * 3
	}
	if (/(^|\+)D(\+|$)/.test(s)) {
		h += 1
	}
	if (/\+M(\+|$)/.test(s)) {
		h += 2
	}
	return h
}

// ─────────────────────────────────────────────────────────────
// Rubricile TEHNICE ale unei fișe RLU — regimul tehnic din certificatul de urbanism (nota A12, 01.10)
// ─────────────────────────────────────────────────────────────

/** O rubrică a fișei, așa cum intră în act: eticheta regulamentului + textul ei. */
export interface FisaRubric {
	label: string
	text: string
}

/** Plierea unei etichete: fără diacritice, litere mici, doar litere/cifre separate prin câte un spațiu. */
export function fold_label(s: string): string {
	return s
		.normalize("NFD")
		.replace(/\p{M}+/gu, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim()
}

/**
 * Lista PERMISĂ de rubrici tehnice, pe eticheta pliată (`fold_label`), în ordinea articolelor unui
 * regulament local: parcelare → aliniament → limitele laterale și posterioare → distanțe între
 * clădiri → amplasarea pe parcelă → circulații și accese → staționare → înălțimea maximă → aspectul
 * exterior → echiparea edilitară → spații libere/verzi → împrejmuiri → detaliile de POT și de CUT.
 * Tiparele sunt ancorate unde o etichetă de alt fel le-ar putea atinge („Zonă de spații verzi, sport
 * și agrement" e o denumire de zonă, nu rubrica „Spații verzi"). Orice etichetă nepotrivită NU intră.
 */
const FISA_TEHNIC_ALLOW: readonly RegExp[] = [
	/^parcelare\b|^caracteristici\w* (ale )?parcel/,
	/aliniament|\baliniere\b/,
	/\blimitele? (laterale|posterioare|parcelei|parcelelor|de proprietate)/,
	/\bdistant\w* (minim\w* )?intre cladiri|\bcladiril\w* unele fata de altele/,
	/^amplasarea (cladirilor |constructiilor )?pe (aceeasi )?parcela/,
	/^circulati|^acces(e|uri|ul|ele)?\b|^cai de acces/,
	/^stationar|^parcar|^parcaje\b/,
	/^inaltime\w* maxim/,
	/^aspect\w* exterior/,
	/\bechipar\w* edilitar|^conditii de echipare/,
	/^spatii (libere|verzi|plantate)/,
	/^imprejmuir/,
	/^(pot|procent\w* (maxim )?de ocupare)\b.*\bdetalii$/,
	/^(cut|coeficient\w* (maxim )?de utilizare)\b.*\bdetalii$/,
]
/** Nu intră niciodată, chiar dacă un tipar de mai sus le-ar atinge: zonele de risc și ZRS. */
const FISA_TEHNIC_DENY = /\brisc|\bzrs\b|reglementare suplimentara/
/** „Nu este cazul" (cu sau fără punct) = rubrică fără conținut, ca în panoul de regim. */
const FISA_NOT_APPLICABLE = /^nu (este|e) cazul$/

/**
 * Categoria tehnică a unei etichete de fișă: indicele din lista permisă sau -1 = NU e rubrică tehnică
 * (utilizările, identificarea și descrierea zonei, zonele de risc și ZRS, precum și rubricile simple
 * de POT, CUT și regim de înălțime, care ar dubla indicatorii din prima propoziție a regimului tehnic).
 */
export function fisa_tehnic_category(label: string): number {
	const folded = fold_label(label)
	if (folded === "" || FISA_TEHNIC_DENY.test(folded)) {
		return -1
	}
	return FISA_TEHNIC_ALLOW.findIndex((re) => re.test(folded))
}

/**
 * Rubricile TEHNICE ale unei fișe RLU (`metadata.rlu_fise[cod]`), cu textul lor, pentru rubrica
 * „regimul tehnic" a certificatului de urbanism. Doar etichetele de pe lista permisă, doar cele cu
 * conținut. ⚠️ Ordinea e cea a ARTICOLELOR regulamentului (ordinea listei permise), nu ordinea cheilor
 * primite: fișa stă în jsonb, iar Postgres întoarce cheile unui obiect jsonb sortate după lungime, nu
 * în ordinea în care au fost scrise — „ordinea fișei" citită din bază e deci întâmplătoare
 * („Împrejmuiri" ar ieși înaintea „Parcelării"). Două etichete din aceeași categorie își păstrează
 * ordinea de la intrare.
 */
export function fisa_tehnic_rubrics(fisa: Record<string, unknown>): FisaRubric[] {
	const hits: Array<{ category: number; index: number; rubric: FisaRubric }> = []
	Object.entries(fisa).forEach(([label, value], index) => {
		const category = fisa_tehnic_category(label)
		if (category < 0 || value == null || typeof value === "object") {
			return
		}
		const text = String(value).trim()
		if (text === "" || FISA_NOT_APPLICABLE.test(fold_label(text))) {
			return
		}
		hits.push({ category, index, rubric: { label: label.trim(), text } })
	})
	return hits.toSorted((a, b) => a.category - b.category || a.index - b.index).map((h) => h.rubric)
}

// ─────────────────────────────────────────────────────────────
// Extravilanul în act (O1, 01.10): textele rubricilor când punctul nu cade în niciun UTR
// ─────────────────────────────────────────────────────────────

/** Atributele de HARTĂ care suprascriu textele implicite (editate în #/admin → Regim UTR). */
export const EXTRAVILAN_ECONOMIC_KEY = "extravilan_regim_economic"
export const EXTRAVILAN_TEHNIC_KEY = "extravilan_regim_tehnic"

export const EXTRAVILAN_ECONOMIC_DEFAULT =
	"Teren situat în extravilanul localității, în afara unităților teritoriale de referință reglementate prin Planul Urbanistic General."
export const EXTRAVILAN_TEHNIC_DEFAULT =
	"Amplasamentul nu se află într-o unitate teritorială de referință: prin Planul Urbanistic General nu sunt stabiliți indicatori urbanistici (POT, CUT, regim de înălțime) pentru acesta."

/**
 * Textele de extravilan ale unei hărți: atributul hărții când e completat, altfel implicitul. Un
 * atribut gol (sau care nu e text) = implicitul — prin REST o cheie EAV nu se poate șterge, deci
 * „gol" e singurul mod de a reveni la textul implicit.
 */
export function extravilan_texts(map_attributes: Record<string, unknown>): {
	economic: string
	tehnic: string
} {
	const text = (key: string): string => {
		const v = map_attributes[key]
		return typeof v === "string" ? v.trim() : ""
	}
	return {
		economic: text(EXTRAVILAN_ECONOMIC_KEY) || EXTRAVILAN_ECONOMIC_DEFAULT,
		tehnic: text(EXTRAVILAN_TEHNIC_KEY) || EXTRAVILAN_TEHNIC_DEFAULT,
	}
}
